import type { NameId } from "./_models";
import { getBranches } from "../../../pages/settings/core/_requests";
import { reachableBranchIds } from "./branchSession";

/** Keep only entries with a real display name (never bare UUID-as-name). */
export function namedOnly(branches: NameId[]): NameId[] {
  return branches.filter((branch) => {
    const name = branch.name?.trim();
    return !!branch.id && !!name && name !== branch.id;
  });
}

/**
 * Switcher branches.
 *
 * Prefer GET /authorization/permissions `branches` (same set as switch-branch;
 * works without branches:read). If the API omits that field (older backends),
 * fall back to by_branch ∩ GET /branch for callers who hold branches:read.
 * GET /branch stays the management list — not the preferred switcher source.
 *
 * `null` / `undefined` → field omitted → fallback.
 * `[]` → authoritative empty switchable set (do not fall back).
 */
export async function hydrateReachableBranches(
  permissionsBranches: NameId[] | undefined | null,
  byBranch?: Record<string, string[]> | undefined | null,
  canReadBranches = true
): Promise<NameId[]> {
  if (permissionsBranches != null) {
    return namedOnly(permissionsBranches);
  }

  const reachableIds = reachableBranchIds(byBranch);
  if (reachableIds.length === 0 || !canReadBranches) {
    return [];
  }

  try {
    const branchesRes = await getBranches();
    const live = new Map<string, string>();
    for (const branch of branchesRes.data.data ?? []) {
      if (branch.id && branch.name?.trim()) {
        live.set(branch.id, branch.name.trim());
      }
    }
    return namedOnly(
      reachableIds
        .filter((id) => live.has(id))
        .map((id) => ({
          id,
          name: live.get(id) || id,
        }))
    );
  } catch {
    return [];
  }
}
